import {DashboardLayout} from '@/modules/layout/ components/dashboard-layout'
import {LicenceCard} from '@/modules/licenses/components/licence-card'
import {SmallLicenceCard} from '@/modules/licenses/components/small-licence-card'
import {useGetOrganisation} from '@/modules/organisations/api'
import {DemoOrganisationBadge, OfflineOrganisationBadge} from '@/modules/organisations/components'
import {IssueLicenceDialog} from '@/modules/organisations/components/issue-licence-dialog'
import {OrganisationUsersSection} from '@/modules/organisations/components/organisation-users-section'
import {OfflineBundles} from '@/modules/organisations/components/offline-bundles'
import {useGetLatestInstallerRelease} from '@/modules/organisations/api/use-get-latest-installer-release'
import {OrganisationStatusToBadgeColorMap} from '@/modules/organisations/constants'
import {OrganisationsSearch} from '@/modules/organisations/schemas'
import {PageHeader} from '@/shared/components/ui/page-header'
import {Badge, Box, Button, Grid, HStack} from '@chakra-ui/react'
import {determineLicenceStatus, isOrganisationOffline, LicenceStatus} from '@cypro/admin-engine/utils'
import {DataSection, dialog, LinkButton, Tooltip} from '@cypro/ui/components'
import {CountryCodeToLabelMap} from '@cypro/ui/utils/countries'
import {createFileRoute} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {LuArrowLeft} from 'react-icons/lu'

export const LicenceStatusToColorMap = {
  [LicenceStatus.enum.upcoming]: 'blue',
  [LicenceStatus.enum.active]: 'cyan',
  [LicenceStatus.enum.expiring]: 'orange',
  [LicenceStatus.enum.expired]: 'red',
}

export const Route = createFileRoute('/organisations/$id')({
  component: OrganisationPage,
  validateSearch: OrganisationsSearch,
})

function OrganisationPage() {
  const {t} = useTranslate('common')
  const {id} = Route.useParams()
  const [organisation] = useGetOrganisation(id)
  const {release} = useGetLatestInstallerRelease()

  if (!organisation) return null

  let pageDesc: string[] = []
  if (organisation.type) pageDesc.push(t(organisation.type))
  if (organisation.country) pageDesc.push(CountryCodeToLabelMap[organisation.country])

  const licence = organisation.licences.at(0)
  const pastLicences = organisation.licences.slice(1)

  return (
    <DashboardLayout>
      <Box pt="8" pb="4">
        <LinkButton to="/organisations" size="sm" variant="plain" colorPalette="cyan" px="0">
          <LuArrowLeft size={16} />
          {t('back_to_organisations')}
        </LinkButton>
      </Box>

      <Grid width="100%" height="100%" gridTemplateColumns="auto 28rem" gapX="16">
        <Box as="section" height="100%" display="flex" flexDir="column" overflow="hidden">
          <HStack height="max-content" alignItems="flex-start" justifyContent="space-between" gapX="5" pt="4" pb="8">
            <PageHeader
              title={
                <HStack>
                  {organisation.name}
                  {organisation.isDemo && <DemoOrganisationBadge />}
                  {isOrganisationOffline(organisation) && <OfflineOrganisationBadge size="md" />}
                </HStack>
              }
              description={pageDesc.join(' • ')}
              mb="4"
            />

            <HStack gapX="2">
              {organisation.deployedVersion && (
                <Badge
                  size="lg"
                  colorPalette={release && release.version !== organisation.deployedVersion ? 'orange' : 'cyan'}
                  borderWidth="1px"
                >
                  {organisation.deployedVersion}
                  {release && <Tooltip content={`latest: ${release.version}`} />}
                </Badge>
              )}
              {organisation.status && (
                <Badge
                  size="lg"
                  colorPalette={OrganisationStatusToBadgeColorMap[organisation.status]}

                  borderWidth="1px"
                >
                  {t(organisation.status)}
                </Badge>
              )}
            </HStack>
          </HStack>

          <OrganisationUsersSection />
        </Box>

        <Box as="section">
          {licence && <LicenceCard licence={licence} />}

          <HStack gapX="2" mt="5">
            <Button
              size="sm"
              colorPalette={licence ? LicenceStatusToColorMap[determineLicenceStatus(licence)] : 'cyan'}
              onClick={() => dialog.open('issue-licence', {children: <IssueLicenceDialog organisationId={id} />})}
            >
              {t('issue_new_licence')}
            </Button>

            <Button asChild size="sm" colorPalette="cyan" variant="outline">
              <a href={`/api/v1/releases/latest/installer?organisationId=${id}`}>
                {t('download_installer')}
                <Tooltip content={t('download_installer_tooltip')} />
              </a>
            </Button>
          </HStack>

          <OfflineBundles />

          {Boolean(pastLicences.length) && (
            <DataSection label={t('history')} labelAs="h3" pt="8" spaceY="2">
              {pastLicences.map(l => (
                <SmallLicenceCard key={l.id} licence={l} />
              ))}
            </DataSection>
          )}
        </Box>
      </Grid>
    </DashboardLayout>
  )
}
